import { defineStore } from 'pinia';
import axios from 'axios';
import User from '@/models/user';
import TablePreferences from '@/models/preferences/table';

const auth = axios.create({
    baseURL: import.meta.env.VITE_APP_ENDPOINT
});
auth.defaults.withCredentials = true;
auth.defaults.withXSRFToken = true;

export const useAuthStore = defineStore('auth', {
    state: () => {
        return {
            authenticated: false,
            user: new User,
            error: null,
            timeout: null
        }
    },
    getters: {
        getUser: (state) => state.user,
        level: (state) => state.user.level,
        prefs: (state) => state.user.meta?.prefs || { tables: [] },
        getTablePrefs: (state) => 
            (view, defaults) => 
                new TablePreferences(state.prefs?.tables.find(table => table.view === view) ?? { view, ...(defaults ?? {}) })
    },
    actions: {
        csrf() {
            return auth.get('sanctum/csrf-cookie');
        },
        login(email, password, redirect) {
            this.csrf().then(() => {
                auth.post('login', {email,password})
                .then(() => {
                    this.check();
                    this.error = null;
                    this.$router.push(redirect ?? {name: 'dashboard'});
                })
                .catch(error => {
                    console.error(error);
                    this.error = error.response.data.message;
                })
            })
            
        },
        logout() {
            auth.post('logout')
                .then(() => {
                    this.authenticated = false;
                    this.user = {};
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.$router.push({name: 'login', redirectedFrom: this.$router.currentRoute.value});
                })
        },
        async check() {
            let session = 60*60*2*1000;
            return await auth.get('api/user')
                .then(response => {
                    this.authenticated = true;
                    this.user = new User(response.data);
                    this.error = null;
                    if(this.timeout) clearTimeout(this.timeout)
                    this.timeout = setTimeout(() => {
                        console.warn('Automatically logging out after session expiration');
                        this.logout();
                    }, session)
                })
                .catch(error => {
                    if(this.authenticated === true) {
                        this.error = error.response.data.message;
                    }
                    this.authenticated = false;
                    this.user = {};
                })
        },
        async setPrefs(prefs) {
            return await auth.patch(`api/users/${this.user.id}/preferences`, {
                prefs
            })
                .then(response => {
                    this.user = new User({...this.user, ...response.data});
                })
                .catch(error => {
                    if(this.authenticated === true) {
                        this.error = error.response.data.message;
                    }
                })
        },
        async setTablePrefs(prefs) {
            let index = this.prefs?.tables.findIndex(table => table.view === prefs.view) ?? -1;
            let newPrefs = this.prefs;
            if(index > -1) {
                newPrefs.tables[index] = prefs;
            } else {
                newPrefs.tables.push(prefs);
            }
            this.setPrefs(newPrefs);
        }
    }
})